import { Code, ConnectError } from "@connectrpc/connect";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import {
  CancelReasonCode as ProtoCancelReasonCode,
  ReservationStatus,
} from "@viajero/proto/guest/v1";
import { ErrorStateView } from "@viajero/shared-ui/error-boundary";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryStateFallback } from "@/components/QueryStateFallback";
import { useCancelReservation } from "@/features/reservation/api/useCancelReservation";
import { useGetReservation } from "@/features/reservation/api/useGetReservation";
import { CancelAgreementSection } from "@/features/reservation/components/CancelAgreementSection";
import { CancelConfirmModal } from "@/features/reservation/components/CancelConfirmModal";
import type { CancelReasonCode } from "@/features/reservation/components/CancelReasonSection";
import { CancelReasonSection } from "@/features/reservation/components/CancelReasonSection";
import { RefundInfoSection } from "@/features/reservation/components/RefundInfoSection";
import { ReservationSummaryCard } from "@/features/reservation/components/ReservationSummaryCard";
import { colors } from "@/theme/colors";

type Props = {
  onCancelComplete: () => void;
};

// キャンセル理由 (UI の 4 択) を proto の CancelReasonCode へ対応付ける。UI には無い
// PRICE_OR_FEE を除く 4 値のみを扱う (§6.6。CancelReasonSection の選択肢に対応)。
const REASON_CODE_MAP: Record<CancelReasonCode, ProtoCancelReasonCode> = {
  cannotAttend: ProtoCancelReasonCode.CANNOT_ATTEND,
  bookOther: ProtoCancelReasonCode.BOOK_OTHER,
  notAsExpected: ProtoCancelReasonCode.NOT_AS_EXPECTED,
  other: ProtoCancelReasonCode.OTHER,
};

/**
 * 予約キャンセル画面 (/reservations/{予約トークン}/cancel)。GetReservation で取得した予約内容
 * (店舗名・日時・購入内容・キャンセルポリシー・返金予定額) を表示し、同意・理由 (必須) を確認して
 * CancelReservation でキャンセルを実行する (§6.6)。
 *
 * 表示・認可はすべて予約アクセストークン (ReservationTokenLayout が interceptor へ渡す) に基づく。
 * 返金予定額 (refund_amount) はエコーバックとして送り、実行時の再算出値と異なる場合 (日付またぎ等。
 * §3.2) は FailedPrecondition となるため、GetReservation を再取得して最新の内容を表示し直す。
 */
export function CancelConfirmStep({ onCancelComplete }: Props) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useGetReservation();
  const cancelReservation = useCancelReservation();

  const [agreed, setAgreed] = useState(false);
  const [reasonCode, setReasonCode] = useState<CancelReasonCode | null>(null);
  const [reasonNote, setReasonNote] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  // 言語切替に追従できるよう、state には翻訳済み文字列ではなく i18n キーを保持する。
  const [submitErrorKey, setSubmitErrorKey] = useState<string | null>(null);

  // 冪等化キー。二重送信・再試行でも同一予約のキャンセルを 1 回に収める (§6.6。Stripe Refund へ伝搬)。
  const idempotencyKeyRef = useRef<string>("");
  if (!idempotencyKeyRef.current) {
    idempotencyKeyRef.current = crypto.randomUUID();
  }

  const reservation = data?.reservation;
  const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;

  if (!reservation) {
    return (
      <QueryStateFallback
        isLoading={isLoading}
        // 取得は成功したが reservation が空のケースも「見つからない」として扱う。
        // 以前は isLoading 側で吸収しておりスピナーが回り続けていた (ShopDetailView と同じ考え方)。
        isNotFound={isNotFound || (!isLoading && !isError)}
        isError={isError}
        notFound={{
          title: t("cancelReservation.notFound"),
          description: t("cancelReservation.notFoundDescription"),
        }}
        error={{
          title: t("cancelReservation.error"),
          description: t("cancelReservation.errorDescription"),
          actions: [{ label: t("errorPage.reload"), onClick: () => void refetch() }],
        }}
      />
    );
  }

  // キャンセルできるのは確定済み (confirmed) の予約のみ。それ以外 (キャンセル済み等) は案内を表示する。
  if (reservation.status !== ReservationStatus.CONFIRMED) {
    return (
      <ErrorStateView
        severity="info"
        title={t("cancelReservation.notCancelable")}
        description={t("cancelReservation.notCancelableDescription")}
      />
    );
  }

  const paidAmount = Number(reservation.totalAmount);
  const cancelFee = Number(reservation.cancelFee ?? 0n);
  const refundAmount = Number(reservation.refundAmount ?? BigInt(paidAmount - cancelFee));

  const handleCancel = async () => {
    if (!reasonCode) return;
    setSubmitErrorKey(null);
    try {
      await cancelReservation.mutateAsync({
        reasonCode: REASON_CODE_MAP[reasonCode],
        reasonNote,
        idempotencyKey: idempotencyKeyRef.current,
        expectedRefundAmount: reservation.refundAmount ?? BigInt(refundAmount),
        languageCode: i18n.language,
      });
      setModalOpen(false);
      onCancelComplete();
    } catch (e) {
      setModalOpen(false);
      // 返金予定額が実行時に変わっていた場合 (§3.2) は最新の内容を取り直して再確認を促す。
      if (e instanceof ConnectError && e.code === Code.FailedPrecondition) {
        void refetch();
        setSubmitErrorKey("cancelReservation.refundChangedError");
        return;
      }
      setSubmitErrorKey("cancelReservation.executeError");
    }
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 4, maxWidth: 640, width: "100%", mx: "auto" }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: colors.text, textAlign: "center", pb: 4 }}
      >
        {t("cancelReservation.title")}
      </Typography>

      <ReservationSummaryCard reservation={reservation} />

      <Divider sx={{ my: 3 }} />

      <CancelAgreementSection agreed={agreed} onAgreedChange={setAgreed} />

      <Divider sx={{ my: 3 }} />

      <CancelReasonSection
        reasonCode={reasonCode}
        reasonNote={reasonNote}
        onReasonCodeChange={setReasonCode}
        onReasonNoteChange={setReasonNote}
      />

      <Divider sx={{ my: 3 }} />

      <RefundInfoSection
        paidAmount={paidAmount}
        cancelFee={cancelFee}
        refundAmount={refundAmount}
        cardLast4={reservation.cardLast4}
      />

      {submitErrorKey && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {t(submitErrorKey)}
        </Alert>
      )}

      <Button
        variant="contained"
        color="error"
        fullWidth
        disabled={!agreed || !reasonCode}
        onClick={() => setModalOpen(true)}
        sx={{ mt: 4, minHeight: 48 }}
      >
        {t("cancelReservation.executeButton")}
      </Button>

      <CancelConfirmModal
        open={modalOpen}
        submitting={cancelReservation.isPending}
        onClose={() => setModalOpen(false)}
        onConfirm={handleCancel}
      />
    </Box>
  );
}
